// The mixer, for a thumb.
//
// Live's mixer is a wall of channel strips and so is this one — a strip per
// track, scrolling sideways when there are more than fit — because a mixer
// that reflows into a list stops being a mixer: you read levels by comparing
// faders at a glance, and that only works when they stand side by side.
//
// Playback only. These faders move what YOU hear; they never propose anything
// to the Set or the room. They start from the Set's own values, so the mix you
// land on is the producer's until you touch it, and `reset` puts a strip back.
//
// The meter, the fader and the pan control are the desktop mixer's own
// (`components/MixerPanel.tsx`) — one set of ballistics and one feel, so a
// level means the same thing wherever it is drawn.

import { CornerUpLeft, RotateCcw, Square } from "lucide-react";
import { faderDbLabel, gainDbLabel, panLabel } from "@shared/mixer.js";
import { clipColor, type Lane } from "@web-shared/lib/arrangement";
import { Meter, PanKnob, VerticalFader } from "@web-shared/components/MixerPanel";
import { cn } from "@web-shared/lib/utils";
import type { LaneLevel } from "@web-shared/lib/audio-engine";
import type { LaneAudioState } from "@web-shared/components/ArrangementView";

/** Tall enough to be worth dragging, short enough to fit a phone with the
 * name, the pan and the buttons above and below it. */
const FADER_H = 180;

export interface MixerLaunchState {
  playing: Record<string, string>;
  overridden: ReadonlySet<string>;
  onStopLane: (laneId: string) => void;
  onBackToArrangement: (laneId?: string) => void;
}

export function MixerView({
  lanes,
  levels,
  onLevel,
  onReset,
  audio,
  launch,
  meters,
  master,
}: {
  lanes: Lane[];
  /** Lane id → what playback is using now. Absent ⇒ the Set's own values. */
  levels: Record<string, LaneLevel>;
  onLevel: (laneId: string, level: LaneLevel) => void;
  /** Put one lane back to the Set's own fader and pan. */
  onReset: (laneId: string) => void;
  audio?: LaneAudioState;
  launch?: MixerLaunchState;
  meters: { level: (laneId: string) => number; master: () => number; active: boolean };
  master: { volume: number; onVolume: (v: number) => void };
}) {
  const soloing = (audio?.soloed.size ?? 0) > 0;

  return (
    <div className="scrollbar-thin -mx-3 overflow-x-auto overscroll-contain px-3 wide:mx-0 wide:px-0">
      <div className="flex min-w-max gap-1.5">
        {lanes.map((lane) => {
          const level = levels[lane.id] ?? { volume: lane.volume, pan: lane.pan };
          const muted = audio?.muted.has(lane.id) ?? false;
          const soloed = audio?.soloed.has(lane.id) ?? false;
          // Silence has two causes and they read differently: you muted it,
          // or someone else's solo won.
          const silenced = muted || (soloing && !soloed);
          const touched =
            Math.abs(level.volume - lane.volume) > 1e-4 || Math.abs(level.pan - lane.pan) > 1e-4;
          return (
            <div
              key={lane.id}
              className={cn(
                "flex w-[132px] shrink-0 flex-col items-center overflow-hidden rounded-2xl border border-[#24242c] bg-[#121217] pb-4",
                silenced && "opacity-50",
              )}
            >
              {/* Color bar */}
              <div className="h-1.5 w-full" style={{ backgroundColor: clipColor((lane.clips[0] ?? lane.sessionClips[0])?.color ?? 0x3a3a3c, lanes.indexOf(lane)) }} />

              {/* Name */}
              <div className="w-full px-3 pt-3 text-center">
                <p className={cn("truncate text-sm font-semibold", silenced && "line-through")} title={lane.name}>
                  {lane.name}
                </p>
                <p className="truncate text-[10px] text-white/40">{lane.kind === "midi" ? "MIDI" : "Audio"}</p>
              </div>

              {/* Pan knob */}
              <div className="mt-3 w-full px-3">
                <PanKnob
                  value={level.pan}
                  disabled={false}
                  onCommit={(v) => onLevel(lane.id, { ...level, pan: v })}
                />
              </div>

              {/* Fader + meter */}
              <div className="mt-3 flex items-end gap-2 px-3">
                <VerticalFader
                  value={level.volume}
                  min={0}
                  max={1}
                  height={FADER_H}
                  disabled={false}
                  title={`${lane.name} — ${faderDbLabel(level.volume)} (double-click to reset)`}
                  onCommit={(v) => onLevel(lane.id, { ...level, volume: v })}
                  onInput={(v) => onLevel(lane.id, { ...level, volume: v })}
                  reset={lane.volume}
                />
                <div className="flex gap-[3px]">
                  <Meter read={() => meters.level(lane.id)} active={meters.active} height={FADER_H} />
                  <Meter read={() => meters.level(lane.id)} active={meters.active} height={FADER_H} />
                </div>
              </div>

              {/* dB readout */}
              <p className={cn("mt-2 font-mono text-xs", level.volume < 0.3 ? "text-white/50" : "text-white/80")}>
                {faderDbLabel(level.volume)}
              </p>

              {/* M/S buttons */}
              <div className="mt-3 flex gap-1.5">
                <Toggle
                  label="M"
                  on={muted}
                  title={`${muted ? "Unmute" : "Mute"} ${lane.name}`}
                  onClick={() => audio?.onMute(lane.id, !muted)}
                />
                <Toggle
                  label="S"
                  on={soloed}
                  title={`${soloed ? "Unsolo" : "Solo"} ${lane.name}`}
                  onClick={() => audio?.onSolo(lane.id, !soloed)}
                />
                {touched && (
                  <button
                    type="button"
                    onClick={() => onReset(lane.id)}
                    title={`Back to the Set's own level — ${faderDbLabel(lane.volume)} ${panLabel(lane.pan)}`}
                    aria-label={`Reset ${lane.name} to the Set's level`}
                    className="flex size-[22px] items-center justify-center rounded-md bg-[#1c1c24] text-white/50 border border-[#2a2a33]"
                  >
                    <RotateCcw className="size-3" />
                  </button>
                )}
              </div>

              <LaneSessionControl lane={lane} launch={launch} />
            </div>
          );
        })}

        {/* The master is a listening control, not the Set's: it is this
            browser's volume, which is why it has no reset to anything. */}
        <div className="flex w-[132px] shrink-0 flex-col items-center overflow-hidden rounded-2xl border border-white/15 bg-[#121217] pb-4 ml-4">
          <div className="h-1.5 w-full bg-[#eaeaef]" />
          <div className="w-full px-3 pt-3 text-center">
            <p className="text-sm font-semibold">Master</p>
            <p className="text-[10px] text-white/40">output</p>
          </div>
          <div className="mt-3 flex items-end gap-2 px-3">
            <VerticalFader
              value={master.volume}
              min={0}
              max={1}
              height={FADER_H}
              disabled={false}
              title="Listening volume"
              onCommit={master.onVolume}
              onInput={master.onVolume}
              reset={1}
            />
            <div className="flex gap-[3px]">
              <Meter read={meters.master} active={meters.active} height={FADER_H} />
              <Meter read={meters.master} active={meters.active} height={FADER_H} />
            </div>
          </div>
          <p className="mt-2 font-mono text-xs text-white/80">{gainDbLabel(master.volume)}</p>
          {launch && (
            <button
              type="button"
              onClick={() => launch.onBackToArrangement()}
              disabled={launch.overridden.size === 0}
              title="Back to Arrangement — every track plays its timeline clips again"
              className="mt-3 flex h-7 w-[104px] items-center justify-center gap-1 rounded-lg bg-[#1c1c24] px-1 text-[10px] text-white/50 disabled:opacity-40"
            >
              <CornerUpLeft className="size-3" />
              all
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One track's Session-View state, as a control.
 *
 * Playing ⇒ ■ stops that lane's launched clip. Overridden but silent ⇒ ↩ hands
 * the lane back to its timeline. Neither ⇒ nothing, because a button that does
 * nothing is worse than an empty space.
 */
export function LaneSessionControl({
  lane,
  launch,
  className,
}: {
  lane: Lane;
  launch?: MixerLaunchState;
  className?: string;
}) {
  if (!launch) return null;
  if (launch.playing[lane.id]) {
    return (
      <button
        type="button"
        onClick={() => launch.onStopLane(lane.id)}
        title={`Stop ${lane.name}`}
        aria-label={`Stop ${lane.name}`}
        className={cn(
          "flex h-7 w-full items-center justify-center gap-1 rounded bg-primary text-[10px] text-primary-foreground",
          className,
        )}
      >
        <Square className="size-3 fill-current" />
        stop
      </button>
    );
  }
  if (launch.overridden.has(lane.id)) {
    return (
      <button
        type="button"
        onClick={() => launch.onBackToArrangement(lane.id)}
        title={`Back to Arrangement — ${lane.name} plays its timeline clips again`}
        aria-label={`Back to Arrangement on ${lane.name}`}
        className={cn(
          "flex h-7 w-full items-center justify-center gap-1 rounded bg-muted text-[10px] text-muted-foreground",
          className,
        )}
      >
        <CornerUpLeft className="size-3" />
        arr
      </button>
    );
  }
  return <span className={cn("h-7 w-full", className)} aria-hidden />;
}

function Toggle({
  label,
  on,
  title,
  onClick,
}: {
  label: string;
  on: boolean;
  title: string;
  onClick: () => void;
}) {
  const isMute = label === "M";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={on}
      className={cn(
        "flex size-[22px] items-center justify-center rounded-md text-[11px] font-bold border transition-colors",
        on
          ? isMute
            ? "bg-[#f0a45b] text-[#1a1206] border-[#f0a45b]"
            : "bg-[#6bb1e2] text-[#06121a] border-[#6bb1e2]"
          : "bg-[#1c1c24] text-[#8a8a96] border-[#2a2a33]",
      )}
    >
      {label}
    </button>
  );
}
